/* */
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, FormFeedback, InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap';
import { TooltipReactTooltip } from 'components/Tooltip';
import { get } from 'lodash';
import { isNumeric, isReactComponent } from 'helpers';
import { CommonIcons } from 'components/Icons';
import DIV from 'components/HtmlTags/DIV';
import { SIZE_ICON } from 'consts/data';
import { cn } from 'helpers/css';
import NewDesign from 'components/NewDesign';

const propTypes = {
  type: PropTypes.string,
  invalid: PropTypes.bool,
  className: PropTypes.string,
  errorField: PropTypes.string,
  maxLength: PropTypes.number,
  style: PropTypes.instanceOf(React.CSSProperties),
  onChangeCustomize: PropTypes.func,
  onKeyDown: PropTypes.func,
  defaultValue: PropTypes.string,
  placeholder: PropTypes.string,
  isHighlight: PropTypes.bool,
  tabIndex: PropTypes.any,

  styleAddOnText: PropTypes.object,
  classNameAddOnText: PropTypes.string,
  enableDefaultOnChange: PropTypes.bool,

  addOnType: PropTypes.oneOf(['prepend', 'append']),
  addOnText: PropTypes.string,

  label: PropTypes.any,
  subLabel: PropTypes.string,
  afterLabel: PropTypes.any,
  autoFocus: PropTypes.bool,
  afterOnChange: PropTypes.func,
  isShowHidePassword: PropTypes.bool,
  classNameContainer: PropTypes.string,
};

const InputField = (props) => {
  //! State
  const {
    form,
    field,
    maxLength,
    placeholder = '',
    type = 'text',
    label = '',
    subLabel = '',
    disabled = false,
    onChangeCustomize,
    onKeyDown,
    style = {},
    invalid = false,
    className = '',
    classNameLabel = '',
    errorField,
    // defaultValue = "",
    addOnText,
    addOnType = 'append', // prepend, append,
    readOnly = false,
    autoComplete = null,
    enableDefaultOnChange = false,

    tooltipLabel = '',
    placeTooltip = 'top',
    required = false,
    tabIndex = undefined,

    styleAddOnText,
    classNameAddOnText = '',
    afterLabel = '',
    value = '',
    autoFocus = false,

    onPaste = () => {}, // for OTP paste
    onBlur,
    onFocus,
    afterOnChange,
    isShowHidePassword,
    classNameContainer,
    hideInvalidMessage,
    innerRef,
    ...restProps
  } = props;
  const {
    name,
    // value = ""
  } = field || {};
  const ref = useRef(null);
  const [showPass, setShowPass] = useState(false);
  const { errors, touched } = form || {};

  const error = get(errors || {}, name);
  const itemTouched = get(touched || {}, name);

  //! Function
  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        ref && ref.current && ref.current.focus();
      }, 200);
    }
  }, [autoFocus]);

  const onChange = (e) => {
    onChangeCustomize && onChangeCustomize(e);
    if (onChangeCustomize && !enableDefaultOnChange) {
      return;
    }

    field && field.onChange(e);
    afterOnChange && afterOnChange(e);
  };

  const toggleShowPassword = () => {
    setShowPass((prev) => !prev);
  };

  //! Render
  const renderError = useCallback(() => {
    if (hideInvalidMessage) {
      return null;
    }

    if (errorField) {
      return <FormFeedback>{errorField}</FormFeedback>;
    }

    if (itemTouched && error) {
      return <FormFeedback>{error}</FormFeedback>;
    }

    return null;
  }, [errorField, error, itemTouched, hideInvalidMessage]);

  const renderLabel = useCallback(() => {
    if (label) {
      if (!!tooltipLabel) {
        return (
          <TooltipReactTooltip place={placeTooltip} idTarget={name} messageToolTip={tooltipLabel}>
            <label
              className={`${classNameLabel} ${required ? 'required' : ''}`}
              htmlFor={name}
              dangerouslySetInnerHTML={isReactComponent(label) ? undefined : { __html: label }}
            >
              {isReactComponent(label) ? label : undefined}
            </label>
            {afterLabel}
            {subLabel && <p className='sub-text mb-2'>{subLabel}</p>}
          </TooltipReactTooltip>
        );
      }

      //* Check mix dangerousInnerHTML vs React Component
      return (
        <Fragment>
          <label
            className={cn(`${classNameLabel} ${required ? 'required' : ''}`, '!tw-mb-1')}
            htmlFor={name}
            dangerouslySetInnerHTML={isReactComponent(label) ? undefined : { __html: label }}
          >
            {isReactComponent(label) ? label : undefined}
          </label>
          {afterLabel}
          {subLabel && <p className='sub-text mb-2'>{subLabel}</p>}
        </Fragment>
      );
    }

    return null;
  }, [classNameLabel, name, label, tooltipLabel, placeTooltip, tooltipLabel]);

  const InputWrapper = isShowHidePassword ? DIV : Fragment;
  const PropsInputWrapper = isShowHidePassword
    ? {
        className: 'input-wrapper w-100 position-relative',
      }
    : undefined;

  const renderMaxLength = () => {
    if (isNumeric(maxLength) && Number(maxLength) > 0) {
      return (
        <div className='d-flex tw-gap-[4px] mt-1 justify-content-end'>
          <NewDesign.Typography variant='subtitle2'>
            {field?.value?.length || 0}
          </NewDesign.Typography>
          <NewDesign.Typography variant='subtitle2'>/</NewDesign.Typography>
          <NewDesign.Typography variant='subtitle2'>{maxLength}</NewDesign.Typography>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={cn('component:InputField', classNameContainer)}>
      {renderLabel()}

      <div className='input-container'>
        <InputGroup>
          {addOnText && addOnType === 'prepend' && (
            <InputGroupAddon addonType={addOnType}>
              <InputGroupText className={classNameAddOnText} style={styleAddOnText}>
                {addOnText}
              </InputGroupText>
            </InputGroupAddon>
          )}

          <InputWrapper {...PropsInputWrapper}>
            <Input
              {...field}
              innerRef={(refInput) => {
                ref && (ref.current = refInput);
                innerRef && (innerRef.current = refInput);
              }}
              onBlur={(e) => {
                field?.onBlur && field.onBlur(e);
                onBlur && onBlur(e);
              }}
              onFocus={(e) => {
                field?.onFocus && field.onFocus(e);
                onFocus && onFocus(e);
              }}
              value={field?.value ?? value}
              style={style}
              className={cn(
                'tw-flex tw-h-9 !tw-rounded-md tw-border tw-border-input tw-bg-transparent tw-px-3 tw-py-1 tw-text-base tw-shadow-sm  file:tw-border-0 file:tw-bg-transparent file:tw-text-sm file:tw-font-medium file:tw-text-foreground placeholder:tw-text-muted-foreground focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-ring disabled:tw-cursor-not-allowed disabled:tw-opacity-50 md:tw-text-sm tw-w-full md:tw-min-w-0',
                addOnText && '!tw-rounded-r-none',
                className,
              )}
              onChange={onChange}
              type={isShowHidePassword ? (showPass ? 'text' : 'password') : type}
              id={name}
              maxLength={maxLength}
              // value={value || defaultValue}
              placeholder={placeholder}
              disabled={disabled}
              invalid={invalid || !!errorField || (!!error && itemTouched)}
              onKeyDown={onKeyDown}
              readOnly={readOnly}
              autoComplete={autoComplete}
              tabIndex={tabIndex}
              onPaste={onPaste}
              {...restProps}
            />

            {isShowHidePassword && (
              <div
                className='position-absolute d-flex align-items-center justify-content-center'
                style={{
                  top: 10,
                  right: invalid || !!errorField || (!!error && itemTouched) ? 30 : 8,
                  width: 18,
                  height: 18,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {showPass ? (
                  <CommonIcons.EyeClose
                    size={SIZE_ICON}
                    className='is-hover'
                    onClick={toggleShowPassword}
                  />
                ) : (
                  <CommonIcons.EyeOpen
                    size={SIZE_ICON}
                    className='is-hover'
                    onClick={toggleShowPassword}
                  />
                )}
              </div>
            )}

            {addOnText && addOnType === 'append' && (
              <InputGroupAddon addonType={addOnType}>
                <InputGroupText className={classNameAddOnText} style={styleAddOnText}>
                  {addOnText}
                </InputGroupText>
              </InputGroupAddon>
            )}
          </InputWrapper>
        </InputGroup>
        {renderError()}
        {renderMaxLength()}
      </div>
    </div>
  );
};

InputField.propTypes = propTypes;

InputField.defaultProps = {
  type: 'text',
  tabIndex: '0',
};

export default InputField;
